import React from 'react'
import {TableHeader} from '../TableHeader'
import Pagination from 'react-responsive-pagination'
import RecordPerPageSelector from '../selectors/RecordPerPageSelector'

// Column definition type
export type ColumnDef<T> = {
  header: string
  accessorKey?: keyof T
  sortKey?: string
  static?: boolean // non-sortable columns
  width?: number
  disabled?: boolean
  className?: string
  headerClassName?: string
  cell?: (row: T, index: number) => React.ReactNode
}

// Pagination configuration
export interface PaginationConfig {
  currentPage: number
  totalPages: number
  totalRecords: number
  take: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
}

// Main DataTable props
export interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T>[]
  loading?: boolean
  emptyMessage?: string

  // Sorting
  sortable?: boolean
  sort?: string
  direction?: 'asc' | 'desc'
  onSortChange?: (sort: string, direction: 'asc' | 'desc') => void

  // Pagination
  pagination?: PaginationConfig

  // Styling
  className?: string
  tableClassName?: string

  // Row key
  rowKey?: keyof T | ((row: T) => string | number)
}

export function DataTable<T>({
  data,
  columns,
  loading = false,
  emptyMessage = 'No data available',
  sortable = true,
  sort = '',
  direction = 'asc',
  onSortChange,
  pagination,
  className = '',
  tableClassName = 'table align-middle gs-0 gy-4',
  rowKey,
}: DataTableProps<T>) {
  // Generate row key
  const getRowKey = (row: T, index: number): string | number => {
    if (typeof rowKey === 'function') {
      return rowKey(row)
    } else if (rowKey) {
      return String(row[rowKey])
    } else if ((row as any).id !== undefined) {
      return (row as any).id
    }
    return index
  }

  // Transform columns to TableHeader format
  const tableHeaderColumns = columns
    .filter((col) => !col.disabled)
    .map((col) => ({
      name: col.header,
      sort: col.sortKey || String(col.accessorKey || ''),
      static: col.static !== false && !col.sortKey && !col.accessorKey ? true : col.static,
      width: col.width,
      disabled: col.disabled,
    }))

  // Handle sort update
  const handleSortUpdate = (newSort: string, newDirection: string) => {
    if (onSortChange) {
      onSortChange(newSort, newDirection as 'asc' | 'desc')
    }
  }

  return (
    <div className={className}>
      <div className='table-responsive'>
        <table className={tableClassName}>
          {sortable && (
            <TableHeader
              sort={sort}
              direction={direction}
              columns={tableHeaderColumns}
              onUpdate={handleSortUpdate}
            />
          )}

          {!sortable && (
            <thead>
              <tr className='fw-bolder text-muted bg-light'>
                {columns
                  .filter((col) => !col.disabled)
                  .map((col, idx) => (
                    <th
                      key={`${col.header}-${idx}`}
                      style={col.width ? {width: `${col.width}%`, borderTop: 'none'} : {borderTop: 'none'}}
                      className={col.headerClassName}
                    >
                      {col.header}
                    </th>
                  ))}
              </tr>
            </thead>
          )}

          {!loading && data.length > 0 && (
            <tbody>
              {data.map((row, rowIndex) => (
                <tr key={getRowKey(row, rowIndex)}>
                  {columns
                    .filter((col) => !col.disabled)
                    .map((col, colIndex) => {
                      // Render cell content
                      let cellContent: React.ReactNode

                      if (col.cell) {
                        // Use custom cell renderer
                        cellContent = col.cell(row, rowIndex)
                      } else if (col.accessorKey) {
                        // Use accessor key to get value
                        const value = row[col.accessorKey]
                        cellContent = value !== null && value !== undefined ? String(value) : '-'
                      } else {
                        cellContent = '-'
                      }

                      return (
                        <td key={`${getRowKey(row, rowIndex)}-${colIndex}`} className={col.className}>
                          {cellContent}
                        </td>
                      )
                    })}
                </tr>
              ))}
            </tbody>
          )}

          {!loading && data.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={columns.filter((col) => !col.disabled).length} className='text-center py-10'>
                  <div className='text-muted fs-5'>{emptyMessage}</div>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      {pagination && (
        <div className='card-footer px-1 py-4'>
          <div className='d-flex justify-content-between'>
            <div style={{minWidth: '200px'}}>
              <RecordPerPageSelector
                placeholder='Rows per page'
                onChange={(name: any, value: any) => {
                  pagination.onPageSizeChange(value)
                }}
              />
            </div>
            <div data-kt-table-toolbar='base'>
              <Pagination
                current={pagination.currentPage}
                total={pagination.totalPages}
                onPageChange={(page: number) => {
                  pagination.onPageChange(page)
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

